/**
 * Firebase API for fetching shared sessions
 */

// Cloud Function endpoint
// Always use production API (local emulators rarely needed for web preview dev)
const API_BASE_URL = 'https://us-central1-emolio-nl.cloudfunctions.net';

/**
 * Shared session data structure
 */
export interface SharedSession {
  shareId: string;
  title: string;
  duration: number;
  location?: string;
  graphData: {
    timestamps: number[];
    values: number[];
    sampleRate: number;
  };
  statistics: {
    totalBits?: number; // Optional - may be missing in old data
    ones: number;
    zeros: number;
    zScore: number;
    pValue: number;
    significance: string;
    effectSizeP?: number;
    anomalyScore?: number;
  };
  audioPreview: {
    status: 'pending' | 'processing' | 'ready' | 'failed' | 'none';
    url?: string;
    waveformUrl?: string;
    duration?: number;
  };
  viewCount: number;
  createdAt: string;
}

/**
 * Validates and fixes session data to ensure all required fields are present
 * Handles backwards compatibility with old session formats
 */
export function validateSessionData(session: any): SharedSession {
  // Ensure graphData exists
  if (!session.graphData) {
    console.warn('Session is missing graphData');
    session.graphData = {
      timestamps: [],
      values: [],
      sampleRate: 0,
    };
  }

  // Ensure statistics exists
  if (!session.statistics) {
    session.statistics = {
      totalBits: 0,
      ones: 0,
      zeros: 0,
      zScore: 0,
      pValue: 1,
      significance: 'not significant',
    };
  }

  // Calculate totalBits if missing
  if (!session.statistics.totalBits) {
    const calculated = (session.statistics.ones || 0) + (session.statistics.zeros || 0);
    const estimated = session.graphData?.values?.length || 0;
    session.statistics.totalBits = calculated || estimated || 0;

    if (session.statistics.totalBits > 0) {
      console.warn(`Calculated missing totalBits: ${session.statistics.totalBits}`);
    }
  }

  return session as SharedSession;
}

/**
 * Fetch a shared session by its share ID
 */
export async function getSharedSession(shareId: string): Promise<SharedSession> {
  const response = await fetch(`${API_BASE_URL}/share-getSharedSession?id=${shareId}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return validateSessionData(data);
}

/**
 * Format a duration to a human-readable string
 * Handles both milliseconds (if > 100000) and seconds
 */
export function formatDuration(duration: number): string {
  // If duration is very large, assume it's in milliseconds and convert to seconds
  // A 24-hour session = 86400 seconds, but in ms = 86,400,000
  // So anything over 100,000 is likely milliseconds
  const seconds = duration > 100000 ? Math.floor(duration / 1000) : duration;

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }

  return `${minutes}m ${secs}s`;
}

/**
 * Format a date string to a human-readable format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get significance level display text
 * Handles both underscore format (not_significant) and space format (not significant)
 */
export function getSignificanceText(significance: string): string {
  // Normalize: convert spaces to underscores for consistent comparison
  const normalized = significance.toLowerCase().replace(/\s+/g, '_');

  switch (normalized) {
    case 'highly_significant':
      return 'Highly Significant';
    case 'very_significant':
      return 'Very Significant';
    case 'significant':
      return 'Significant';
    case 'marginally_significant':
    case 'marginal':
      return 'Marginally Significant';
    default:
      return 'Not Significant';
  }
}

/**
 * Normalize significance value to underscore format
 * Used for CSS class names and consistent data handling
 */
export function normalizeSignificance(significance: string): string {
  return significance.toLowerCase().replace(/\s+/g, '_');
}

/**
 * User profile data structure for web display
 */
export interface WebUserProfile {
  userId: string;
  displayName: string;
  anonymizedEmail?: string;
  photoURL?: string;
  bio?: string;
  location?: {
    locality?: string;
    country?: string;
  } | null;
  isDJ: boolean;
  djProfile?: {
    stageName?: string;
    genres?: string[];
    experience?: string;
    socialLinks?: {
      website?: string;
      soundcloud?: string;
      mixcloud?: string;
      spotify?: string;
      instagram?: string;
    };
    availableForBooking?: boolean;
  } | null;
  sessionCount: number;
  publicSessionCount: number;
  totalBitsGenerated: number;
}

/**
 * Public session data for profile display
 */
export interface WebPublicSession {
  id: string;
  shareId: string;
  title: string;
  duration: number;
  location?: string;
  startTime: string;
  statistics?: {
    totalBits: number;
    zScore: number;
    significance: string;
  };
}

/**
 * Profile response from API
 */
export interface ProfileResponse {
  profile: WebUserProfile;
  sessions: WebPublicSession[];
}

/**
 * Fetch a user's public profile by their user ID
 */
export async function getUserProfile(userId: string): Promise<ProfileResponse> {
  const response = await fetch(`${API_BASE_URL}/profile-getProfileForWeb?userId=${userId}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Discovery profile (minimal data for listing)
 */
export interface DiscoveryProfile {
  userId: string;
  displayName: string;
  anonymizedEmail?: string;
  photoURL?: string;
  bio?: string;
  location?: {
    locality?: string;
    country?: string;
  } | null;
  isDJ: boolean;
  djProfile?: {
    stageName?: string;
    genres?: string[];
  } | null;
  sessionCount: number;
  publicSessionCount: number;
  totalBitsGenerated: number;
}

/**
 * Discovery session (minimal data for listing)
 */
export interface DiscoverySession {
  shareId: string;
  title: string;
  duration: number;
  location?: string;
  // Location coordinates for map view
  locationData?: {
    latitude: number;
    longitude: number;
    locality?: string;
    country?: string;
  } | null;
  createdAt: string | null;
  statistics?: {
    totalBits: number;
    zScore: number;
    significance: string;
  } | null;
  hasAudio: boolean;
  audioStatus: string;
  userDisplayName: string;
  userId: string;
  // Category information
  category?: string;
  categoryName?: string;
  categoryEmoji?: string;
}

export interface SessionsResponse {
  sessions: DiscoverySession[];
  total: number;
  hasMore: boolean;
  lastCursor: string | null;
}

/**
 * Fetch list of public profiles for discovery
 */
export async function listPublicProfiles(limit = 20): Promise<{ profiles: DiscoveryProfile[]; total: number }> {
  const response = await fetch(`${API_BASE_URL}/profile-listPublicProfiles?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch list of recent public sessions for discovery
 * Supports pagination via startAfter cursor
 */
export async function listPublicSessions(limit = 20, startAfter?: string): Promise<SessionsResponse> {
  let url = `${API_BASE_URL}/profile-listPublicSessions?limit=${limit}`;
  if (startAfter) {
    url += `&startAfter=${encodeURIComponent(startAfter)}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Public event data for discovery listing
 */
export interface PublicEvent {
  eventId: string;
  joinCode: string;
  title: string;
  status: 'lobby' | 'active' | 'ended' | 'archived';
  organizerName: string;
  participantCount: number;
  maxDevices: number;
  tier: string;
  categoryId?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    locality?: string;
    country?: string;
    geohash?: string;
  };
  scheduledStartTime?: string;
  createdAt: string | null;
  description?: string;
  totalSessionsRecorded: number;
  totalBitsGenerated: number;
}

export interface EventsResponse {
  events: PublicEvent[];
  total: number;
  hasMore: boolean;
  lastCursor: string | null;
}

/**
 * Fetch list of public events for discovery
 * Supports pagination and status filtering
 */
export async function listPublicEvents(
  limit = 20,
  startAfter?: string,
  status?: string
): Promise<EventsResponse> {
  let url = `${API_BASE_URL}/trngEvents-listPublicEvents?limit=${limit}`;
  if (startAfter) url += `&startAfter=${encodeURIComponent(startAfter)}`;
  if (status) url += `&status=${encodeURIComponent(status)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}
